import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { api, fmtMoney, fmtDate, STAGE_META, formatError, isAdmin } from '../api';
import { useAuth } from '../AuthContext';
import { Banknote, TrendingUp, Briefcase, DollarSign, Lock } from 'lucide-react';

export default function Accounts() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [deals, setDeals] = useState([]);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!isAdmin(user)) return;
    api.get('/dashboard').then((r) => setData(r.data)).catch((e) => setErr(formatError(e)));
    api.get('/deals?limit=500').then((r) => setDeals(r.data)).catch(() => {});
  }, [user]);

  if (user === null) return <div className="crm-card">Loading…</div>;
  if (!isAdmin(user)) {
    return (
      <div className="crm-card" style={{ textAlign: 'center', padding: 40 }} data-testid="accounts-denied">
        <Lock size={32} color="#94a3b8" style={{ marginBottom: 10 }} />
        <h3 style={{ margin: 0, color: 'var(--crm-ink)' }}>Admin access required</h3>
        <p style={{ color: 'var(--crm-muted)', fontSize: 13, margin: '6px 0 0' }}>
          Financial reports and margin data are visible to admins only.
        </p>
      </div>
    );
  }

  if (err) return <div className="crm-card" style={{ color: '#9f1239' }}>{err}</div>;
  if (!data) return <div className="crm-card">Loading…</div>;

  // Filter only revenue-generating stages (won + in-flight after approval)
  const billable = deals.filter((d) => !['lost', 'new_lead', 'contacted', 'proposal_sent', 'drawings_requested'].includes(d.stage));
  const totals = billable.reduce(
    (a, d) => ({
      revenue: a.revenue + Number(d.client_quote || 0),
      cost: a.cost + Number(d.estimator_cost || 0),
    }),
    { revenue: 0, cost: 0 }
  );
  const margin = totals.revenue - totals.cost;
  const marginPct = totals.revenue > 0 ? Math.round((margin / totals.revenue) * 100) : 0;

  // Top deals by margin
  const topDeals = [...billable]
    .map((d) => ({ ...d, margin: Number(d.client_quote || 0) - Number(d.estimator_cost || 0) }))
    .sort((a, b) => b.margin - a.margin)
    .slice(0, 10);

  return (
    <div data-testid="crm-accounts">
      <div className="crm-kpi-grid">
        <Kpi label="Revenue (active + won)" value={fmtMoney(totals.revenue)} sub={`${billable.length} deals`} icon={DollarSign} cls="k-emerald" />
        <Kpi label="Estimator Cost" value={fmtMoney(totals.cost)} sub="Total paid to outside estimators" icon={Briefcase} cls="k-rose" />
        <Kpi label="Gross Margin" value={fmtMoney(margin)} sub={`${marginPct}% margin`} icon={TrendingUp} cls="k-indigo" />
        <Kpi label="Payments Received" value={fmtMoney(data.payments_all_time)} sub={`This month ${fmtMoney(data.payments_this_month)}`} icon={Banknote} cls="k-amber" />
      </div>

      <div className="crm-card" style={{ marginTop: 18 }}>
        <h3>Top deals by margin</h3>
        <table className="crm-table">
          <thead>
            <tr>
              <th>Company</th>
              <th>Project</th>
              <th>Stage</th>
              <th style={{ textAlign: 'right' }}>Revenue</th>
              <th style={{ textAlign: 'right' }}>Cost</th>
              <th style={{ textAlign: 'right' }}>Margin</th>
              <th style={{ textAlign: 'right' }}>%</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {topDeals.length === 0
              ? <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--crm-muted)', padding: 30 }}>No active or won deals yet.</td></tr>
              : topDeals.map((d) => {
                const meta = STAGE_META[d.stage] || { label: d.stage, color: '#64748b', light: '#e2e8f0' };
                const pct = Number(d.client_quote) > 0 ? Math.round((d.margin / Number(d.client_quote)) * 100) : 0;
                return (
                  <tr key={d.id}>
                    <td><b>{d.company}</b></td>
                    <td>{d.project_name || '—'}</td>
                    <td><span className="crm-pill" style={{ background: meta.light, color: meta.color }}><span className="dot" style={{ background: meta.color }} />{meta.label}</span></td>
                    <td style={{ textAlign: 'right' }}>{fmtMoney(d.client_quote)}</td>
                    <td style={{ textAlign: 'right' }}>{fmtMoney(d.estimator_cost)}</td>
                    <td style={{ textAlign: 'right', color: 'var(--crm-emerald)', fontWeight: 800 }}>{fmtMoney(d.margin)}</td>
                    <td style={{ textAlign: 'right', color: 'var(--crm-muted)' }}>{pct}%</td>
                    <td style={{ color: 'var(--crm-muted)' }}>{fmtDate(d.created_at)}</td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, icon: Icon, cls = '' }) {
  return (
    <div className={`crm-kpi ${cls}`}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <span className="label">{label}</span>{Icon && <Icon size={16} color="#94a3b8" />}
      </div>
      <div className="value">{value}</div>
      <div className="delta">{sub}</div>
    </div>
  );
}
